/**
 * Customization Fields — Lap Label
 *
 * Shows/hides customization fields when the selected Size variant contains "Custom".
 * Locks quantity to 1 and hides the quantity selector when customization is active.
 * Handles the color dropdown (mirrors the product's Color option availability).
 * Disables the Add to Cart button until required fields are filled.
 */

document.addEventListener('DOMContentLoaded', () => {
  initCustomizationFields();
});

function initCustomizationFields() {
  document.querySelectorAll('.customization-fields').forEach((container) => {
    if (container.dataset.initialized === 'true') return;
    container.dataset.initialized = 'true';

    const sectionId = container.dataset.sectionId;
    const productInfo = container.closest('product-info') || container.closest('[data-section]');

    // Set up the color dropdown
    initCustomizationColorDropdown(container, sectionId);

    // Set up validation that disables/enables the submit button
    initCustomizationValidation(container, sectionId, productInfo);

    // Check initial state
    checkSizeVariant(container, sectionId, productInfo);

    // Listen for variant option changes via the hidden selects
    const variantSelects = productInfo?.querySelector('variant-selects');
    if (variantSelects) {
      variantSelects.addEventListener('change', () => {
        requestAnimationFrame(() => {
          checkSizeVariant(container, sectionId, productInfo);
        });
      });
    }

    // Also listen for the pub/sub event as a fallback
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, () => {
        requestAnimationFrame(() => {
          checkSizeVariant(container, sectionId, productInfo);
        });
      });
    }
  });
}

function checkSizeVariant(container, sectionId, productInfo) {
  const sizeValue = getSizeOptionValue(productInfo);

  if (sizeValue && sizeValue.toLowerCase().includes('custom')) {
    showCustomizationFields(container, sectionId, productInfo);
  } else {
    hideCustomizationFields(container, sectionId, productInfo);
  }
}

function getSizeOptionValue(productInfo) {
  if (!productInfo) return null;

  // Check all variant dropdowns for the Size option
  const dropdownInputs = productInfo.querySelectorAll('.product-form__input--dropdown');
  for (const input of dropdownInputs) {
    const label = input.querySelector('.form__label');
    if (label && label.textContent.trim().toLowerCase().includes('size')) {
      // Check our custom dropdown toggle text first
      const toggle = input.querySelector('.variant-image-dropdown__toggle');
      if (toggle) {
        const selected = toggle.querySelector('.variant-image-dropdown__selected .variant-image-dropdown__text');
        if (selected) return selected.textContent.trim();
      }
      // Fall back to the hidden native select
      const select = input.querySelector('select');
      if (select) return select.value;
    }
  }

  // Also check button/swatch pickers for Size
  const fieldsets = productInfo.querySelectorAll('fieldset.product-form__input');
  for (const fieldset of fieldsets) {
    const legend = fieldset.querySelector('legend');
    if (legend && legend.textContent.trim().toLowerCase().includes('size')) {
      const checked = fieldset.querySelector('input[type="radio"]:checked');
      if (checked) return checked.value;
    }
  }

  return null;
}

function showCustomizationFields(container, sectionId, productInfo) {
  container.style.display = '';

  // Enable form inputs so they submit
  const line1 = container.querySelector(`#CustomizationLine1-${sectionId}`);
  const line2 = container.querySelector(`#CustomizationLine2-${sectionId}`);
  const colorInput = container.querySelector(`#CustomizationColorValue-${sectionId}`);
  const colorToggle = container.querySelector(`#CustomizationColorToggle-${sectionId}`);

  if (line1) { line1.disabled = false; }
  if (line2) { line2.disabled = false; }
  if (colorInput) { colorInput.disabled = false; }
  if (colorToggle) { colorToggle.disabled = false; }

  // Lock quantity to 1 and hide quantity selector
  if (productInfo) {
    const qtySelector = productInfo.querySelector('.product-form__quantity, quantity-input');
    if (qtySelector) {
      qtySelector.style.display = 'none';
      const qtyInput = qtySelector.querySelector('input[name="quantity"]') || productInfo.querySelector('input[name="quantity"]');
      if (qtyInput) {
        qtyInput.value = 1;
        qtyInput.setAttribute('readonly', '');
      }
    }
    const qtyBlock = productInfo.querySelector('[class*="quantity_selector"]');
    if (qtyBlock) qtyBlock.style.display = 'none';
  }

  // Validate immediately to update submit button state
  validateCustomizationFields(container, sectionId, productInfo);
}

function hideCustomizationFields(container, sectionId, productInfo) {
  container.style.display = 'none';

  // Disable form inputs so they don't submit
  const line1 = container.querySelector(`#CustomizationLine1-${sectionId}`);
  const line2 = container.querySelector(`#CustomizationLine2-${sectionId}`);
  const colorInput = container.querySelector(`#CustomizationColorValue-${sectionId}`);
  const colorToggle = container.querySelector(`#CustomizationColorToggle-${sectionId}`);

  if (line1) { line1.disabled = true; line1.value = ''; line1.classList.remove('customization-fields__error'); }
  if (line2) { line2.disabled = true; line2.value = ''; }
  if (colorInput) { colorInput.disabled = true; colorInput.value = ''; }
  if (colorToggle) { colorToggle.disabled = true; colorToggle.classList.remove('customization-fields__error'); }

  // Reset color dropdown display
  const toggleSelected = container.querySelector('.customization-color-dropdown .variant-image-dropdown__selected');
  if (toggleSelected) {
    toggleSelected.innerHTML = '<span class="variant-image-dropdown__text customization-color-placeholder">Please choose a text color</span>';
  }
  // Clear list selection
  container.querySelectorAll('.variant-image-dropdown__option.is-selected').forEach((o) => {
    o.classList.remove('is-selected');
    o.setAttribute('aria-selected', 'false');
  });

  // Restore quantity selector
  if (productInfo) {
    const qtySelector = productInfo.querySelector('.product-form__quantity, quantity-input');
    if (qtySelector) {
      qtySelector.style.display = '';
      const qtyInput = qtySelector.querySelector('input[name="quantity"]') || productInfo.querySelector('input[name="quantity"]');
      if (qtyInput) {
        qtyInput.removeAttribute('readonly');
      }
    }
    const qtyBlock = productInfo.querySelector('[class*="quantity_selector"]');
    if (qtyBlock) qtyBlock.style.display = '';
  }

  // Re-enable submit button since customization is not active
  enableSubmitButton(productInfo);
}

function validateCustomizationFields(container, sectionId, productInfo) {
  // Only validate if customization fields are visible
  if (container.style.display === 'none') return true;

  let isValid = true;

  const line1 = container.querySelector(`#CustomizationLine1-${sectionId}`);
  if (line1 && !line1.value.trim()) {
    isValid = false;
  }

  const colorInput = container.querySelector(`#CustomizationColorValue-${sectionId}`);
  if (colorInput && !colorInput.value) {
    isValid = false;
  }

  if (isValid) {
    enableSubmitButton(productInfo);
  } else {
    disableSubmitButton(productInfo);
  }

  return isValid;
}

function disableSubmitButton(productInfo) {
  if (!productInfo) return;
  const submitBtn = productInfo.querySelector('#ProductSubmitButton-' + productInfo.dataset?.section)
    || productInfo.querySelector('[type="submit"][name="add"]');
  if (submitBtn) {
    submitBtn.setAttribute('aria-disabled', 'true');
    submitBtn.setAttribute('data-customization-blocked', 'true');
  }
}

function enableSubmitButton(productInfo) {
  if (!productInfo) return;
  const submitBtn = productInfo.querySelector('#ProductSubmitButton-' + productInfo.dataset?.section)
    || productInfo.querySelector('[type="submit"][name="add"]');
  if (submitBtn && submitBtn.getAttribute('data-customization-blocked') === 'true') {
    submitBtn.removeAttribute('aria-disabled');
    submitBtn.removeAttribute('data-customization-blocked');
  }
}

function initCustomizationColorDropdown(container, sectionId) {
  const dropdown = container.querySelector('.customization-color-dropdown');
  if (!dropdown) return;

  const toggle = dropdown.querySelector(`#CustomizationColorToggle-${sectionId}`);
  const list = dropdown.querySelector(`#CustomizationColorList-${sectionId}`);
  const hiddenInput = container.querySelector(`#CustomizationColorValue-${sectionId}`);

  if (!toggle || !list) return;

  toggle.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Close any other open variant dropdowns (but not ours)
    if (typeof closeAllVariantDropdowns === 'function') {
      closeAllVariantDropdowns();
    }

    const isOpen = toggle.getAttribute('aria-expanded') === 'true';
    if (isOpen) {
      toggle.setAttribute('aria-expanded', 'false');
      list.classList.remove('is-open');
    } else {
      toggle.setAttribute('aria-expanded', 'true');
      list.classList.add('is-open');
    }
  });

  list.querySelectorAll('.variant-image-dropdown__option').forEach((option) => {
    option.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();

      // Don't allow selecting unavailable colors
      if (option.classList.contains('is-unavailable')) return;

      const value = option.dataset.value;

      // Update selected state
      list.querySelectorAll('.variant-image-dropdown__option').forEach((o) => {
        o.classList.remove('is-selected');
        o.setAttribute('aria-selected', 'false');
      });
      option.classList.add('is-selected');
      option.setAttribute('aria-selected', 'true');

      // Update toggle display
      const toggleSelected = toggle.querySelector('.variant-image-dropdown__selected');
      if (toggleSelected) {
        const imgEl = option.querySelector('.variant-image-dropdown__thumb');
        const textEl = option.querySelector('.variant-image-dropdown__text');
        let selectedHtml = '';
        if (imgEl) {
          selectedHtml += `<img class="variant-image-dropdown__thumb" src="${imgEl.src}" alt="${value}" width="40" height="40" loading="lazy">`;
        }
        if (textEl) {
          selectedHtml += `<span class="variant-image-dropdown__text">${textEl.innerHTML}</span>`;
        }
        toggleSelected.innerHTML = selectedHtml;
      }

      // Update hidden input
      if (hiddenInput) {
        hiddenInput.value = value;
      }

      // Close dropdown
      toggle.setAttribute('aria-expanded', 'false');
      list.classList.remove('is-open');

      // Clear error and re-validate
      toggle.classList.remove('customization-fields__error');
      const productInfo = container.closest('product-info') || container.closest('[data-section]');
      validateCustomizationFields(container, sectionId, productInfo);
    });
  });

  // Close on outside click — use a separate handler that excludes our dropdown
  document.addEventListener('click', (e) => {
    if (!e.target.closest(`#CustomizationColorDropdown-${sectionId}`)) {
      toggle.setAttribute('aria-expanded', 'false');
      list.classList.remove('is-open');
    }
  });

  // Keyboard
  toggle.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggle.click();
    } else if (e.key === 'Escape') {
      toggle.setAttribute('aria-expanded', 'false');
      list.classList.remove('is-open');
    }
  });
}

function initCustomizationValidation(container, sectionId, productInfo) {
  // Listen for input changes to enable/disable the submit button
  const line1 = container.querySelector(`#CustomizationLine1-${sectionId}`);
  if (line1) {
    line1.addEventListener('input', () => {
      if (line1.value.trim()) {
        line1.classList.remove('customization-fields__error');
      }
      validateCustomizationFields(container, sectionId, productInfo);
    });
  }
}
